import { inject, PendingTasks, Service } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode
} from '@angular/common/http';
import { Router } from '@angular/router';
import { EMPTY, from, Observable, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { ApiError } from '@core/errors/api-error';
import { BadRequestApiError } from '@core/errors/bad-request.api-error';
import { ForbiddenApiError } from '@core/errors/forbidden.api-error';
import { NotFoundApiError } from '@core/errors/not-found.api-error';
import { ServerApiError } from '@core/errors/server.api-error';
import { UnauthenticatedApiError } from '@core/errors/unauthenticated.api-error';
import { UnknownApiError } from '@core/errors/unknown.api-error';
import { Location } from '@core/services/location';
import { logging } from '@utils/logging';

@Service({ autoProvided: false })
export class ApiErrorInterceptor implements HttpInterceptor {
  private locationService = inject(Location);
  private router = inject(Router);
  private pendingTasks = inject(PendingTasks);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Capture a stack trace before the request
    const stackTrace = new Error().stack;
    // Handle the request
    return next.handle(req).pipe(catchError((error: unknown) => this.handle$(error, stackTrace)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handle$(error: unknown, stackTrace?: string): Observable<any> {
    // Unknown error, ignore
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }

    // Normalize the body of the error. This is needed because the body might be an ArrayBuffer, Blob, JSON, string or empty.
    // Register the async work as a pending task so the application is not reported as stable before the error is mapped.
    const removeTask = this.pendingTasks.add();
    const resolve = normalizeErrorBody(error.error)
      .finally(removeTask)
      .catch(e => {
        // If we fail to normalize the error body, we log the error and return null
        logging.unknownError(e, { context: 'normalizeErrorBody' });
        return null;
      });

    return from(resolve).pipe(
      mergeMap(body => {
        // Map the error to an ApiError
        const apiError = mapHttpErrorToApiError(error, body);
        // Override the stack trace of the ApiError
        apiError.stack = stackTrace;
        // Try to handle the error or rethrow it
        return this.tryHandleApiErrors$(apiError);
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private tryHandleApiErrors$(error: ApiError): Observable<any> {
    // Handle unauthenticated errors, the user is not logged in
    if (error instanceof UnauthenticatedApiError) {
      // Redirect to login page
      this.locationService.assign('/user/login');
      // Return an empty observable to prevent the error from propagating
      return EMPTY;
    }

    // Handle Cloudflare challenge errors
    if (error instanceof ForbiddenApiError && error.headers.get('cf-mitigated') === 'challenge') {
      const newError = new Error('Cloudflare challenge on API request. Hard reloading the page.');
      newError.cause = error;
      logging.warn(newError, { url: error.url, status: error.status, innerMessage: error.message });
      // Hard reload the current page to force new Cloudflare challenge
      this.locationService.assign(this.router.url);
      // Return an empty observable to prevent the error from propagating
      return EMPTY;
    }

    return throwError(() => error);
  }
}

function mapHttpErrorToApiError(error: HttpErrorResponse, body: unknown): ApiError {
  switch (error.status as HttpStatusCode) {
    case HttpStatusCode.BadRequest:
      return new BadRequestApiError(error, body);
    case HttpStatusCode.Unauthorized:
      return new UnauthenticatedApiError(error, body);
    case HttpStatusCode.Forbidden:
      return new ForbiddenApiError(error, body);
    case HttpStatusCode.NotFound:
      return new NotFoundApiError(error, body);
    case HttpStatusCode.InternalServerError:
    case HttpStatusCode.BadGateway:
    case HttpStatusCode.ServiceUnavailable:
    case HttpStatusCode.GatewayTimeout:
      return new ServerApiError(error, body);
    default:
      return new UnknownApiError(error, body);
  }
}

export async function normalizeErrorBody(body: unknown): Promise<unknown> {
  if (body === null || body === undefined) {
    return null;
  }

  // The type of body is going to depend on the responseType(https://angular.io/api/common/http/HttpRequest#responseType) of the original request.
  // The body might be arraybuffer, blob, json, text or empty.

  // If the body is a blob we decode it to a string
  if (body instanceof Blob) {
    body = await body.text();
  }

  // If the body is a ArrayBuffer we decode it to a string
  if (body instanceof ArrayBuffer) {
    const decoder = new TextDecoder('utf-8');
    body = decoder.decode(body);
  }

  // If the body is a string and looks like JSON, we parse it
  if (
    typeof body === 'string' &&
    ((body.startsWith('{') && body.endsWith('}')) || (body.startsWith('"') && body.endsWith('"')))
  ) {
    body = JSON.parse(body);
  }

  // Otherwise we return the body as is
  return body;
}
